import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

import { classNames } from '@/utils/classNames';

import styles from './Tip.module.css';

type TipProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

export function Tip({ label, children, className }: TipProps): JSX.Element {
  const tooltipId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const isOpen = isHovered || isPinned;

  useEffect(() => {
    if (!isPinned) {
      return;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;

      if (rootRef.current && target instanceof Node && !rootRef.current.contains(target)) {
        setIsPinned(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsPinned(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPinned]);

  return (
    <div
      ref={rootRef}
      className={classNames(styles.tip, className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className={styles.tipTrigger}
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls={tooltipId}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={() => setIsPinned((current) => !current)}
      >
        <Info size={14} strokeWidth={2.3} />
      </button>

      {isOpen ? (
        <div id={tooltipId} role="tooltip" className={styles.tipBubble}>
          <div className={styles.tipBubbleContent}>{children}</div>
        </div>
      ) : null}
    </div>
  );
}
